import { ActiveSelection, FabricCanvas, FabricObject } from "../objects";
import { LayerAlignmentType } from "../interfaces";
import Layer, { LayerProps } from "../classes/Layer";
import {
  CalendarLayer,
  ClockLayer,
  ClipArtLayer,
  CrosswordLayer,
  CurvedTextLayer,
  WarpedTextLayer,
  CustomImageLayer,
  ImageLayer,
  Group,
  WordSearchLayer,
  lockObject,
  QRCodeLayer,
  TextLayer,
  updateObjectPosition,
  updateActiveSelection,
} from "..";

export const alignLayer = (
  layer: FabricObject,
  alignment: LayerAlignmentType,
) => {
  const canvas = layer?.canvas;
  const clipPath = canvas?.clipPath;
  if (!canvas || !clipPath) return;
  const { width, height, scaleX, scaleY } = layer;
  const properties = {
    left: { left: 0 },
    "horizontal-center": { left: (clipPath.width - width * scaleX) / 2 },
    right: { left: clipPath.width - width * scaleX },
    top: { top: 0 },
    "vertical-center": { top: (clipPath.height - height * scaleY) / 2 },
    bottom: { top: clipPath.height - height * scaleY },
    center: {
      left: (clipPath.width - width * scaleX) / 2,
      top: (clipPath.height - height * scaleY) / 2,
    },
    fit: {
      left: 0,
      top: 0,
      width: clipPath.width,
      height: clipPath.height,
    },
  };
  updateObjectPosition(layer);
  layer.set(properties[alignment]);
  layer.setCoords();
  layer.fire("modified");
  canvas.requestRenderAll();
};

export const pasteLayers = async (
  canvas: FabricCanvas,
  layersProps: LayerProps[],
) => {
  if (layersProps.length === 0) return;
  const layers = layersProps.map((props) => {
    const { _id, name, x, y, ...rest } = props;
    return createLayer({
      ...rest,
      name: `${name} (cloned)`,
      removable: true,
      x: x + (canvas.clipPath?.width || 0) * 0.1,
      y: y + (canvas.clipPath?.height || 0) * 0.1,
    });
  });
  canvas.add(...layers);
  canvas.requestRenderAll();

  const target = layersToTarget(layers);
  canvas.fire("object:modified", {
    action: "add",
    target: target,
  });
};

export const createLayer = (layer: Partial<LayerProps>) => {
  switch (layer.customType) {
    case "image":
      return new ImageLayer(layer);
    case "custom-image":
      return new CustomImageLayer(layer);
    case "text":
      return new TextLayer(layer);
    case "curved-text":
      return new CurvedTextLayer(layer);
    case "warped-text":
      return new WarpedTextLayer(layer);
    case "clip-art":
      return new ClipArtLayer(layer);
    case "calendar":
      return new CalendarLayer(layer);
    case "clock":
      return new ClockLayer(layer);
    case "qr-code":
      return new QRCodeLayer(layer);
    case "crossword":
      return new CrosswordLayer(layer);
    case "word-search":
      return new WordSearchLayer(layer);
    default:
      return new Layer(layer);
  }
};

export const lockLayer = async (
  layer: Layer | ActiveSelection,
  locked: boolean = false,
) => {
  lockObject(layer, locked);
  layer.fire("modified");
  if (layer instanceof ActiveSelection) {
    layer.getObjects().forEach((l) => {
      lockObject(l, locked);
      l.fire("modified");
    });
  } else {
    if (locked) {
      if (layer.group) {
        layer.group.remove(layer);
        layer.fire("deselected");
      }
    }
  }
  if (layer.group instanceof ActiveSelection) {
    updateActiveSelection(layer.group);
  }
  layer.canvas?.fire("selection:updated");
  layer.canvas?.requestRenderAll();
};

export const showLayer = async (
  layer: Layer | ActiveSelection,
  visible: boolean,
) => {
  layer.set({ visible });
  layer.fire("modified");
  if (layer instanceof ActiveSelection) {
    layer.getObjects().forEach((l) => {
      l.set({ visible });
      l.fire("modified");
    });
  }
  layer.canvas?.requestRenderAll();
};

export const getNextFunctionalId = (canvas: FabricCanvas) => {
  const layers = canvas.getObjects().filter((l) => l instanceof Layer);
  return layers.length + 1;
};

export const selectLayer = (layer: Layer | Group) => {
  const canvas = layer.canvas;
  if (!canvas) return;

  const selectionMode = canvas.selectionMode;
  const currentActiveLayers = canvas
    .getActiveObjects()
    .filter((o) => o instanceof Layer);

  let layersToSelect: Layer[] = [];

  switch (selectionMode) {
    case "single": {
      const activeObject =
        layer instanceof Layer ? layer : layer.getSelection();
      if (!activeObject) return;
      canvas.setActiveObject(activeObject);
      canvas.requestRenderAll();
      return;
    }
    case "multiple": {
      if (layer instanceof Group) {
        const currentActiveLayersSet = new Set(currentActiveLayers);
        const groupLayers = layer.getLayers();
        const groupLayersSet = new Set(groupLayers);
        const groupSelected = groupLayers.every((l) =>
          currentActiveLayersSet.has(l),
        );
        if (groupSelected) {
          layersToSelect = currentActiveLayers.filter(
            (l) => !groupLayersSet.has(l),
          );
        } else {
          layersToSelect = [...currentActiveLayers, ...groupLayers];
          canvas.fire("group:focus", { target: layer });
        }
      } else {
        const exist = currentActiveLayers.includes(layer);
        layersToSelect = exist
          ? currentActiveLayers.filter((l) => l !== layer)
          : [...currentActiveLayers, layer];
        if (layersToSelect.length === 0) layersToSelect = [layer];
      }

      break;
    }
    case "range": {
      if (layer instanceof Group) return;
      const firstLayer = currentActiveLayers?.[0] || layer;
      const layers = canvas.getObjects().filter((o) => o instanceof Layer);
      const startIndex = layers.indexOf(firstLayer);
      const endIndex = layers.indexOf(layer);
      const factor = startIndex < endIndex ? 1 : -1;
      for (let i = startIndex; i != endIndex + factor; i += factor) {
        const l = layers[i];
        if (l instanceof Layer) layersToSelect.push(l);
      }
      break;
    }
  }

  const activeSelection = layersToTarget(layersToSelect);
  canvas.setActiveObject(activeSelection);
  canvas.requestRenderAll();
};

export const layersToTarget = (layers: Layer[]) => {
  const target = new ActiveSelection(layers, { canvas: layers[0]?.canvas });
  updateActiveSelection(target);
  return target;
};

export const processLayersInBatches = (
  layers: Layer[],
  batchSize = 20,
): Promise<LayerProps[]> => {
  return new Promise((resolve) => {
    const modifiedLayers: LayerProps[] = [];
    let index = 0;

    function processBatch() {
      const end = Math.min(index + batchSize, layers.length);

      for (let i = index; i < end; i++) {
        const l = layers[i];
        l.fire("modified");
        modifiedLayers.push(l.toObject());
      }

      index = end;

      if (index < layers.length) {
        setTimeout(processBatch, 0);
      } else {
        resolve(modifiedLayers);
      }
    }

    processBatch();
  });
};
